import { computed, ref, useContext, useStore } from '@nuxtjs/composition-api'
import { cartService } from '@propeller-commerce/propeller-sdk-v2'
import type { Cart as SdkCart, CartUpdateInput } from '@propeller-commerce/propeller-sdk-v2'
import { getPropellerClient, isUnusableCartError, propellerConfig, readCartId } from './client'
import { Cart, CartItem } from './getters'
import { CartState } from '~/store/cart'

/**
 * Cart composable (v2).
 *
 * Same consumer surface as before: `{ cart, loading, error, load, clear,
 * setCart, addItem, removeItem, updateItemQty, updateCart, applyCoupon,
 * removeCoupon }`.
 *
 * Backed by the SDK `cartService(client).*` operations (was hand-rolled
 * mutations). The service methods return the `Cart` directly, so there is no
 * more `response.data.cartX.cart` unwrapping.
 *
 * Semantics kept:
 * - cart state is shared app-wide (Vuex `cart` module: per-request on the
 *   server, hydrated to the client).
 * - the cart id lives in the `propeller-vsf-cart` cookie (name kept from the VSF
 *   era). It is read through `readCartId`, which ignores ids CartV2 cannot
 *   accept — a v1 cart id left in that cookie is not a UUID and made every cart
 *   call fail with `Validation failed (uuid is expected)`.
 * - `load()` no-ops when a cart is already in state.
 * - methods never throw; failures are logged and stored on `error`.
 *
 * Must be called inside `setup()`.
 */

interface AddItemProduct {
  productId?: number
  bundleId?: number | string
}

type StoredCart = CartState['cart']

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export function useCart () {
  const store = useStore<{ cart: CartState }>()
  const { app } = useContext()
  const loading = ref(false)
  const error = ref<Record<string, unknown>>({})

  const cart = computed<StoredCart>(() => store.state.cart.cart)
  const setCart = (newCart: StoredCart): void => {
    store.commit('cart/SET_CART', newCart)
  }

  const service = () => cartService(getPropellerClient())

  const getCookieCartId = (): string | undefined => readCartId(app.$cookies)

  // Common args for the localized cart operations. `imageSearchFilters` is
  // required by the SDK variable types; an empty object lets the backend apply
  // its defaults.
  const localized = () => ({
    language: propellerConfig.language,
    imageSearchFilters: {}
  })

  const load = async (): Promise<void> => {
    if (cart.value) {
      return
    }
    loading.value = true
    try {
      const cartId = getCookieCartId()
      if (!cartId) {
        setCart({})
        return
      }
      const result = await service().getCart({ cartId, ...localized() })
      setCart((result as unknown as StoredCart) ?? null)
      error.value.load = null
    } catch (err) {
      if (isUnusableCartError(err)) {
        // Cart gone upstream: forget it and show an empty cart, so the shop
        // stays usable and the next add-to-cart starts a fresh one.
        app.$cookies.remove(propellerConfig.cartCookieName, { path: '/' })
        setCart({})
        error.value.load = null
        return
      }
      error.value.load = err
      // eslint-disable-next-line no-console
      console.error('useCart.load', err)
    } finally {
      loading.value = false
    }
  }

  /** The cart id from the cookie, or a freshly started cart's id (persisted). */
  const ensureCartId = async (): Promise<string> => {
    const existing = getCookieCartId()
    if (existing) {
      return existing
    }
    const started = await service().startCart({
      input: { language: propellerConfig.language },
      ...localized()
    })
    app.$cookies.set(propellerConfig.cartCookieName, started.cartId, {
      maxAge: COOKIE_MAX_AGE,
      path: '/'
    })
    return started.cartId
  }

  const sendAddItem = async (cartId: string, product: AddItemProduct, quantity: number): Promise<SdkCart> => {
    if (product.bundleId) {
      return await service().addBundleToCart({
        id: cartId,
        input: { bundleId: String(product.bundleId), quantity },
        ...localized()
      })
    }
    // Guard against a missing identifier: without this the SDK silently
    // sends `productId: undefined` and the backend fails with a confusing
    // "Product with ID ... not found". A bundle that forgot to map its id
    // (v2 renamed `comboId` → `id`) would land here.
    if (product.productId == null) {
      throw new Error('useCart.addItem: product has neither a valid productId nor bundleId')
    }
    return await service().addItemToCart({
      id: cartId,
      input: { productId: product.productId, quantity },
      ...localized()
    })
  }

  const addItem = async ({ product, quantity }: { product: AddItemProduct, quantity: number }): Promise<void> => {
    loading.value = true
    try {
      let result: SdkCart
      try {
        result = await sendAddItem(await ensureCartId(), product, quantity)
      } catch (err) {
        if (!isUnusableCartError(err)) {
          throw err
        }
        // The cart the cookie points at is gone (deleted/expired upstream), so
        // forget it and add to a fresh one instead of leaving the shopper
        // permanently unable to add anything.
        app.$cookies.remove(propellerConfig.cartCookieName, { path: '/' })
        result = await sendAddItem(await ensureCartId(), product, quantity)
      }
      setCart(result as unknown as StoredCart)
      error.value.addItem = null
    } catch (err) {
      error.value.addItem = err
      // eslint-disable-next-line no-console
      console.error('useCart.addItem', err)
    } finally {
      loading.value = false
    }
  }

  const removeItem = async ({ product }: { product: CartItem }): Promise<void> => {
    loading.value = true
    try {
      const result = await service().deleteCartItem({
        id: (cart.value as Cart)?.cartId as string,
        input: { itemId: product.itemId },
        ...localized()
      })
      setCart(result as unknown as StoredCart)
      error.value.removeItem = null
    } catch (err) {
      error.value.removeItem = err
      // eslint-disable-next-line no-console
      console.error('useCart.removeItem', err)
    } finally {
      loading.value = false
    }
  }

  const updateItemQty = async ({ product, quantity }: { product: CartItem, quantity: number }): Promise<void> => {
    if (!quantity || quantity <= 0) {
      return
    }
    loading.value = true
    try {
      const result = await service().updateCartItem({
        id: (cart.value as Cart)?.cartId as string,
        itemId: product.itemId,
        input: { quantity },
        ...localized()
      })
      setCart(result as unknown as StoredCart)
      error.value.updateItemQty = null
    } catch (err) {
      error.value.updateItemQty = err
      // eslint-disable-next-line no-console
      console.error('useCart.updateItemQty', err)
    } finally {
      loading.value = false
    }
  }

  const clear = async (): Promise<void> => {
    app.$cookies.remove(propellerConfig.cartCookieName)
    setCart({})
    await Promise.resolve()
  }

  const applyCoupon = async ({ couponCode }: { couponCode: string }): Promise<void> => {
    loading.value = true
    try {
      const result = await service().addActionCodeToCart({
        id: (cart.value as Cart)?.cartId as string,
        input: { actionCode: couponCode },
        ...localized()
      })
      setCart(result as unknown as StoredCart)
      error.value.applyCoupon = null
    } catch (err) {
      error.value.applyCoupon = err
      // eslint-disable-next-line no-console
      console.error('useCart.applyCoupon', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Raw `cartUpdate` (payment method, postage, notes, …). Like the old API it
   * returns the raw cart and does NOT mutate shared state; callers pass the
   * result to `setCart` themselves.
   */
  const updateCart = async (params: { cartId?: string } & Record<string, unknown>) => {
    const { cartId, ...rest } = params
    return await service().updateCart({
      id: (cartId ?? (cart.value as Cart)?.cartId) as string,
      input: rest as unknown as CartUpdateInput,
      language: propellerConfig.language
    })
  }

  const removeCoupon = async (): Promise<void> => {
    loading.value = true
    try {
      const result = await service().removeActionCodeFromCart({
        id: (cart.value as Cart)?.cartId as string,
        input: { actionCode: (cart.value as Cart)?.actionCode as string },
        ...localized()
      })
      setCart(result as unknown as StoredCart)
      error.value.removeCoupon = null
    } catch (err) {
      error.value.removeCoupon = err
      // eslint-disable-next-line no-console
      console.error('useCart.removeCoupon', err)
    } finally {
      loading.value = false
    }
  }

  return {
    cart,
    loading,
    error,
    load,
    clear,
    setCart,
    addItem,
    removeItem,
    updateItemQty,
    updateCart,
    applyCoupon,
    removeCoupon
  }
}
